/* eslint-disable id-length */
import { GroupStep } from "~/lib/pipeline";
import BaseNode from "~/components/Playground/Node/BaseNode";
import { Node, NodeProps } from "@xyflow/react";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import { MissingStepDependencies } from "~/components/Playground/Node/MissingStepDependencies";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import Emojify from "~/components/shared/Emojify";
import { memo, MouseEvent, useCallback } from "react";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import Icon from "~/components/shared/Icon";
import { GroupStepCount } from "~/components/build/Show/components/GroupStepCount";
import classNames from "classnames";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";

type GroupStepNodeType = Node<GroupStep, "group">;

function GroupStepNode(props: NodeProps<GroupStepNodeType>) {
  const { build } = useBuild({ strict: false });
  const isGroupStepExpanded = useBuildStore((state) => state.isGroupStepExpanded);
  const expandGroupSteps = useBuildStore((state) => state.expandGroupSteps);
  const collapseGroupSteps = useBuildStore((state) => state.collapseGroupSteps);
  const { missingDependencies } = useStepDependencies(props.id);

  /**
   * Expand group step when clicking on the node.
   *
   * Don't collapse the group step when clicking on it, as this would be
   * counter-intuitive for users. Instead, we only expand the group step
   * when it is not already expanded.
   */
  const handleNodeClick = useCallback(() => {
    if (!isGroupStepExpanded(props.id)) {
      expandGroupSteps([props.id]);
    }
  }, [isGroupStepExpanded, expandGroupSteps, props.id]);

  const toggleGroup = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();

      isGroupStepExpanded(props.id) ? collapseGroupSteps([props.id]) : expandGroupSteps([props.id]);
    },
    [isGroupStepExpanded, expandGroupSteps, collapseGroupSteps, props.id],
  );

  return (
    <HoverCard.Root>
      <HoverCard.Portal>
        {build && missingDependencies.length && props.data.state && (
          <HoverCard.Content
            state={props.data.state}
            outcome={props.data.outcome}
            missingDependencies={missingDependencies}
          >
            <MissingStepDependencies missingDependencies={missingDependencies} build={build} />
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        <div className="h-full w-full max-w-none bg-white" onClick={handleNodeClick}>
          <BaseNode
            // For groups steps the node width and height is determined by ELK layout on parent element.
            // Therefore, we need to set width and height to 100% to allow the node to fill the available space.
            className={classNames("group h-full w-full max-w-none hover:no-underline", {
              "bg-white/40": isGroupStepExpanded(props.id),
            })}
            {...props}
            label={<Emojify text={props.data.label} />}
            count={build ? <GroupStepCount step={props.data} /> : undefined}
            actions={
              <button
                onClick={toggleGroup}
                className="text-charcoal-700 flex items-center rounded-sm border-transparent bg-gray-100 text-xs transition-transform duration-75 hover:border-purple-600 hover:bg-purple-100 hover:text-purple-600 active:translate-y-0.5 active:bg-purple-100 active:text-purple-600"
                title={isGroupStepExpanded(props.id) ? "Collapse" : "Expand"}
              >
                <Icon icon="heroicons/solid/chevron-up-down" className="block h-5 w-5" />
              </button>
            }
          />
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}

export default memo(GroupStepNode);
